import { createFileRoute, Outlet, useNavigate, Link, useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { getProfile, getPeople } from "@/lib/relationships.functions";
import { getAccessStatus } from "@/lib/access.functions";
import { Users, Settings, Sparkles, Search, Plus, X, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { MobileNav } from "@/components/mobile-nav";
import { PlanetMark } from "@/components/planet-mark";
import { PyngLoader } from "@/components/pyng-loader";
import { ContactSheetProvider, useContactSheet } from "@/components/contact-sheet-provider";
import { AddDrawer } from "@/components/add-drawer";
import { TripSheet } from "@/components/travel/trip-sheet";
import { CreateNewMenu, type CreateAction } from "@/components/create-new-menu";
import { ContactDetailBody } from "@/components/contact-detail-body";
import { useTheme } from "@/hooks/use-theme";
import { groupColor } from "@/lib/group-colors";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { openAddDrawer, setAddDrawerOpen, useAddDrawer } from "@/lib/add-drawer-store";

export const Route = createFileRoute("/_authenticated")({ component: Shell });

function Shell() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [checked, setChecked] = useState(false);
  const fetchProfile = useServerFn(getProfile);
  const fetchAccess = useServerFn(getAccessStatus);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  const { data: profile, isLoading, isFetching } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: () => fetchProfile(),
    enabled: !!user,
  });

  // Invite gate: applies to every sign-in path, including Google and Apple.
  const { data: access, isLoading: accessLoading } = useQuery({
    queryKey: ["access-status", user?.id],
    queryFn: () => fetchAccess(),
    enabled: !!user,
  });

  useEffect(() => {
    if (access && !access.approved) navigate({ to: "/welcome", replace: true });
  }, [access, navigate]);

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (!profile) return;
    if (accessLoading || !access?.approved) return;
    if (!profile.onboarded && loc.pathname !== "/onboarding") {
      navigate({ to: "/onboarding", replace: true });
      return;
    }
    setChecked(true);
  }, [profile, isLoading, isFetching, access, accessLoading, loc.pathname, navigate]);

  if (loading || !user || !checked) {
    return <PyngLoader fullscreen caption="Getting your day ready…" size={84} />;
  }

  return (
    <ContactSheetProvider>
      <ShellLayout />
    </ContactSheetProvider>
  );
}

const nav = [
  { to: "/today", label: "Today", icon: Sparkles },
  { to: "/people", label: "People", icon: Users },
  { to: "/settings", label: "Settings", icon: Settings },
] as const;

function ShellLayout() {
  const loc = useLocation();
  const navigate = useNavigate();
  const fetchPeople = useServerFn(getPeople);
  const { data } = useQuery({ queryKey: ["people"], queryFn: () => fetchPeople() });
  const { open: addOpen, tab: addTab } = useAddDrawer();
  const setAddOpen = setAddDrawerOpen;
  const setAddTab = (t: "person" | "context" | "group" | "trip") => openAddDrawer(t);
  const [q, setQ] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const { selection, close, railEnabled } = useContactSheet();
  const { resolvedTheme } = useTheme();
  const isLightTheme = resolvedTheme === "light";
  const isAdmin = useIsAdmin();

  const categories = data?.categories ?? [];
  const contacts = data?.contacts ?? [];
  const countFor = (id: string) =>
    contacts.filter((p: any) => (p.group_ids ?? (p.category_id ? [p.category_id] : [])).includes(id)).length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submitSearch = (value: string) => {
    navigate({ to: "/people", search: value.trim() ? { q: value.trim() } : {} });
  };

  return (
    <>
      <div className="mx-auto flex min-h-screen max-w-7xl gap-6 px-4 pb-24 pt-[calc(env(safe-area-inset-top)+0.5rem)] md:px-8 md:py-6 md:pb-6">
        <aside className="sticky top-6 hidden h-[calc(100vh-3rem)] w-60 shrink-0 flex-col glass rounded-3xl p-4 md:flex">
          <Link to="/today" className="flex items-center gap-2 px-2 py-1">
            <PlanetMark size={28} className="rounded-lg" />
            <span className="text-sm font-medium tracking-tight">Pyng</span>
          </Link>

          <form
            className="mt-4 flex items-center gap-2 rounded-xl glass-soft px-2.5 py-2"
            onSubmit={(e) => {
              e.preventDefault();
              submitSearch(q);
            }}
          >
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              aria-label="Search your people"
              className="w-full min-w-0 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
            {q && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => { setQ(""); submitSearch(""); }}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </form>

          <nav className="mt-4 space-y-1">
            {nav.map(({ to, label, icon: Icon }) => {
              const active = loc.pathname === to || (to === "/people" && loc.pathname.startsWith("/contacts"));
              return (
                <Link
                  key={to}
                  to={to}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-foreground/[0.06] text-foreground"
                      : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  {label}
                </Link>
              );
            })}
          </nav>

          {categories.length > 0 && (
            <div className="mt-6 min-h-0 flex-1 overflow-y-auto no-scrollbar">
              <div className="flex items-center justify-between px-3 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Groups</span>
                <button
                  type="button"
                  aria-label="Add to a group"
                  onClick={() => {
                    setAddTab("group");
                    setAddOpen(true);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="space-y-0.5">
                {categories.map((c: any, i: number) => {
                  const count = countFor(c.id);
                  if (count === 0) return null;
                  return (
                    <Link
                      key={c.id}
                      to="/people"
                      search={{ group: c.id }}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
                    >
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ background: groupColor(i, isLightTheme) }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate">{c.name}</span>
                      <span className="shrink-0 opacity-60">{count}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="mt-4">
              <div className="px-3 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Admin</span>
              </div>
              <Link
                to="/admin"
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
                  loc.pathname === "/admin"
                    ? "bg-foreground/[0.06] text-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
                )}
              >
                <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
                Admin console
              </Link>
            </div>
          )}

          <CreateNewMenu
            onSelect={(action: CreateAction) => {
              setAddTab(action);
              setAddOpen(true);
            }}
          >
            <button
              type="button"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-blue px-3 py-2.5 text-sm font-medium text-accent-blue-foreground shadow-sm transition-colors hover:bg-accent-blue/90"
            >
              <Plus className="h-4 w-4" strokeWidth={2} /> Create new
            </button>
          </CreateNewMenu>
        </aside>

        <main className="relative min-w-0 flex-1 animate-in-fade">
          <Outlet />
        </main>

        {railEnabled && selection && loc.pathname !== "/admin" && (
          <aside className="no-scrollbar sticky top-6 hidden h-[calc(100vh-3rem)] w-[24rem] shrink-0 overflow-y-auto glass rounded-3xl p-4 xl:block">
            <ContactDetailBody
              contactId={selection.id}
              mode="sheet"
              onClose={close}
              drawerContext={selection.context}
            />
          </aside>
        )}
      </div>

      <AddDrawer
        open={addOpen && addTab !== "trip"}
        onOpenChange={setAddOpen}
        categories={categories}
        initialTab={addTab === "trip" ? "person" : addTab}
      />
      <TripSheet
        open={addOpen && addTab === "trip"}
        onOpenChange={setAddOpen}
        knownLocations={contacts.map((c: any) => c.location).filter(Boolean)}
      />
      <MobileNav />
    </>
  );
}